import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Mui from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { Redirect } from "react-router-dom";
import moment from "moment";
import {
  Add,
  AddCircleOutline,
  CancelOutlined,
  CheckCircleOutline,
  CurrencyRupee,
  FileUploadOutlined,
  HourglassEmptyOutlined,
  KeyboardArrowDown,
  ListAltOutlined,
} from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { defaultAllSelection } from "services/constants";
import { tooltipStyleProps } from "services/constants/Styles";
import { Loader } from "components/Loader";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import {
  GlobalBreadcrumb,
  GlobalConfirmDialog,
  sharedStickyFooterBarSx,
  sharedTableBodyCellSx,
  sharedTableHeadCellSx,
} from "components/DesignSystem/sharedUiPatterns";
import {
  APP_BAR_OFFSET,
  StatCard,
  designColors,
  designShell,
  designType,
  paginationFooter,
  rowsPerPageOptions,
  withDesignFont,
} from "services/constants/designTokens";
import {
  cancelPayInputClaim,
  createPayInputClaim,
  fetchPayInputClaims,
  fetchPayInputSummary,
  fetchReimbursementTypes,
  requestReimbursementSettings,
  updatePayInputClaim,
  useReimbursementSettings,
} from "pages/PayRoll/Shared/reimbursementApi";
import { OpenSettingsButton } from "pages/PayRoll/Shared/settingsSwitch";
import { EmployeeCell } from "../Shared/tableUi";
import { StatusChip, claimHeaders, cycleText, formatDate, money, shownAmount } from "./reimbursementPayUi";
import { ReimbursementFormPanel, ReimbursementViewPanel } from "./ReimbursementPanels";
import { ReimbursementImport } from "./ReimbursementImport";

// Payroll > Pay Inputs > Reimbursements: every employee's out-of-pocket
// claims that are paid with salary — claims raised in My Workspace arrive
// automatically, plus ones HR adds or imports here.
// API: /api/payroll/pay-input/reimbursement/ (Reimbursement_PayInputs_API.pdf,
// 5 Nov 2026): summary/ for the cards, claims/ for the table (all pages, so
// the standard toolbar filters on the screen), claims/{id}/ for the detail,
// add / edit / cancel, import (dry run, then save with import_key).
// Approval stays in Team Workspace. Settings (allowed sources, Approval
// Required, bill upload) and types come from the configuration API. When
// Enable Reimbursements is off this URL moves on to the next Pay Inputs
// screen, like the other pay inputs.

const CARDS = [
  { key: "total", title: "Total", icon: ListAltOutlined, accent: "#2878FF", tint: "#EAF2FF" },
  { key: "pending", title: "Pending", icon: HourglassEmptyOutlined, accent: "#F97316", tint: "#FFF4E5" },
  { key: "ready", title: "Ready", icon: CheckCircleOutline, accent: "#12B76A", tint: "#E7F8EF" },
  { key: "processed", title: "Processed", icon: CurrencyRupee, accent: "#2878FF", tint: "#EAF2FF" },
  { key: "rejected", title: "Rejected", icon: CancelOutlined, accent: "#DC2626", tint: "#FEF2F2" },
];

const headSx = { ...sharedTableHeadCellSx, position: "static", whiteSpace: "nowrap" };
const cellSx = { ...sharedTableBodyCellSx, ...designType.tableBodyCell };
const NUMERIC = new Set(["shown_amount"]);

const cardValue = (card) => (card ? `₹${money(card.amount)} · ${card.count.toLocaleString("en-IN")}` : "—");

export const PayInputReimbursements = () => {
  const { domain, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  const { enabled } = useReimbursementSettings(domain, topbarCompanyId);
  if (!enabled) return <Redirect to="/admin/payroll/pay-inputs/penalties" />;
  return <ReimbursementsScreen key={`${domain}:${topbarCompanyId}`} />;
};

const ReimbursementsScreen = () => {
  const { domain, topbarCompanyId, topbarCompanyName } = useSelector((state) => state.tokenReducer);
  const validCompany = Number(topbarCompanyId) > 0 && Number(topbarCompanyId) !== defaultAllSelection;

  // Settings + types (configuration API).
  const [base, setBase] = useState({ loading: true, error: "", types: [], settings: null });
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    if (!validCompany || !domain) { setBase((prev) => ({ ...prev, loading: false })); return () => controller.abort(); }
    setBase((prev) => ({ ...prev, loading: true, error: "" }));
    Promise.all([
      fetchReimbursementTypes({ domain, companyId: topbarCompanyId, signal: controller.signal }),
      requestReimbursementSettings({ domain, companyId: topbarCompanyId, signal: controller.signal }),
    ])
      .then(([types, { config }]) => { if (!controller.signal.aborted) setBase({ loading: false, error: "", types, settings: config }); })
      .catch((error) => { if (!controller.signal.aborted) setBase((prev) => ({ ...prev, loading: false, error: error.message })); });
    return () => controller.abort();
  }, [domain, topbarCompanyId, validCompany, retry]);

  // Cards + claims (Pay Inputs API).
  const [claims, setClaims] = useState([]);
  const [summary, setSummary] = useState(null);
  const [list, setList] = useState({ loading: true, error: "" });
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    if (!validCompany || !domain) return undefined;
    const controller = new AbortController();
    setList({ loading: true, error: "" });
    Promise.all([
      fetchPayInputClaims({ domain, companyId: topbarCompanyId, signal: controller.signal }),
      fetchPayInputSummary({ domain, companyId: topbarCompanyId, signal: controller.signal }),
    ])
      .then(([rows, cards]) => {
        if (controller.signal.aborted) return;
        setClaims(rows);
        setSummary(cards);
        setList({ loading: false, error: "" });
      })
      .catch((error) => { if (!controller.signal.aborted) setList({ loading: false, error: error.message }); });
    return () => controller.abort();
  }, [domain, topbarCompanyId, validCompany, refresh]);
  const reload = () => setRefresh((n) => n + 1);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [viewId, setViewId] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState(null); // { claim } (claim null = add)
  const [formErrors, setFormErrors] = useState({});
  const [cancelRow, setCancelRow] = useState(null);
  const [cancelRemarks, setCancelRemarks] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [addAnchor, setAddAnchor] = useState(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  const { types, settings } = base;
  // Add / Import follow the allowed sources in Settings. When Settings does
  // not offer a source at all, the item stays and the server answers
  // SOURCE_DISABLED if it is off.
  const sources = settings?.sources || [];
  const offered = (settings?.sourceOptions || []).map((option) => option.value);
  const sourceOn = (code) => !offered.includes(code) || sources.includes(code);
  const manualAllowed = sourceOn("MANUAL");
  const importAllowed = sourceOn("IMPORT");
  const approvalRequired = settings?.approvalRequired === "yes";

  // ---- list rows (flattened for CustomFilter) ----
  const listRows = useMemo(() => claims.map((claim) => ({
    ...claim,
    bill_date_label: formatDate(claim.billDate),
    employee_label: `${claim.employee.name} (${claim.employee.code})`,
    shown_amount: shownAmount(claim),
    payroll_cycle_name: claim.payrollCycle?.name || "—",
  })), [claims]);

  const [filtered, setFiltered] = useState(null);
  const filteredSource = useRef(null);
  const visibleRows = filtered !== null && filteredSource.current === listRows ? filtered : listRows;
  const onFilteredChange = useCallback((data) => {
    filteredSource.current = listRows;
    setFiltered(data);
    setPage(0);
  }, [listRows]);
  const pageRows = visibleRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const run = async (fn) => {
    if (busyRef.current) return false;
    busyRef.current = true;
    setBusy(true);
    try {
      await fn();
      return true;
    } catch (error) {
      showToast("error", error.message);
      return false;
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const saveClaim = async ({ values, files, changes }) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setFormErrors({});
    try {
      if (form?.claim) {
        if (!Object.keys(changes).length) { setForm(null); return; }
        await updatePayInputClaim({ domain, companyId: topbarCompanyId, id: form.claim.id, changes });
        showToast("success", "Reimbursement saved.");
      } else {
        const saved = await createPayInputClaim({ domain, companyId: topbarCompanyId, values, files });
        showToast("success", `Reimbursement ${saved.requestNo} added as ${saved.statusLabel || (approvalRequired ? "Pending" : "Ready")}.`);
      }
      setForm(null);
      reload();
    } catch (error) {
      setFormErrors(error.fieldErrors || {});
      showToast("error", error.message);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const confirmCancel = async () => {
    let message = "";
    const ok = await run(async () => {
      const result = await cancelPayInputClaim({ domain, companyId: topbarCompanyId, id: cancelRow.id, remarks: cancelRemarks.trim() });
      message = result?.message || `Claim ${cancelRow.requestNo} cancelled.`;
    });
    if (!ok) return;
    showToast("success", message);
    setCancelRow(null);
    setViewId(null);
    reload();
  };

  if (!validCompany) {
    return (
      <Mui.Box sx={{ ...designShell.pageSurface, p: 2 }}>
        <Mui.Alert severity="info">Select a single company to view reimbursements.</Mui.Alert>
      </Mui.Box>
    );
  }
  if (base.loading) return <Loader info="Loading..." minHeight="60vh" />;
  if (base.error) {
    return (
      <Mui.Box sx={{ ...designShell.pageSurface, p: 2 }}>
        <Mui.Alert severity="error" action={<Mui.Button color="inherit" onClick={() => setRetry((n) => n + 1)}>Retry</Mui.Button>}>{base.error}</Mui.Alert>
      </Mui.Box>
    );
  }

  const viewRow = listRows.find((row) => row.id === viewId) || null;

  const renderCell = (row, key) => {
    switch (key) {
      case "employee_label": return <EmployeeCell name={row.employee.name} code={row.employee.code} />;
      case "shown_amount":
        return row.amountApproved != null && row.amountApproved !== row.amount ? (
          <Mui.Tooltip title={`Claimed ₹${money(row.amount)}, approved ₹${money(row.amountApproved)}`} arrow slotProps={tooltipStyleProps}>
            <span>{money(row.shown_amount)}</span>
          </Mui.Tooltip>
        ) : money(row.shown_amount);
      case "statusLabel": return <StatusChip row={row} />;
      case "payroll_cycle_name":
        return row.payrollCycle?.expected ? (
          <Mui.Tooltip title="Expected cycle if approved today" arrow slotProps={tooltipStyleProps}>
            <span>{cycleText(row.payrollCycle)}</span>
          </Mui.Tooltip>
        ) : cycleText(row.payrollCycle);
      default: return row[key] || "—";
    }
  };

  const addItems = [
    ...(manualAllowed ? [{ label: "Add Reimbursement", icon: AddCircleOutline, onClick: () => { setFormErrors({}); setForm({ claim: null }); } }] : []),
    // The template is downloaded from inside the Import panel.
    ...(importAllowed ? [{ label: "Import Reimbursements", icon: FileUploadOutlined, onClick: () => setImportOpen(true) }] : []),
  ];

  return (
    <ThemeProvider theme={withDesignFont}>
      <Mui.Box sx={{ ...designShell.pageSurface, pb: 2 }}>
        <Mui.Box sx={{ ...designShell.headerRowBase, alignItems: { xs: "flex-start", sm: "center" }, px: 2, pt: 2, mb: 2 }}>
          <Mui.Box sx={{ minWidth: 0 }}>
            <GlobalBreadcrumb trail={["Payroll", "Pay Inputs", "Reimbursements"]} />
            <Mui.Typography component="h1" sx={designType.pageTitle}>Reimbursements</Mui.Typography>
            <Mui.Typography sx={designType.pageSubtitle}>Employee out-of-pocket claims paid with salary.</Mui.Typography>
          </Mui.Box>
          <Mui.Stack direction="row" spacing={1.25} alignItems="center" sx={{ flexWrap: "wrap", rowGap: 1 }}>
            {/* "Add Reimbursement ▾" menu, same as Bonus / Add Penalty. Its
                items follow the allowed sources in Settings. */}
            {addItems.length ? (
              <Mui.Button
                startIcon={<Add />}
                endIcon={<KeyboardArrowDown />}
                disabled={busy}
                onClick={(event) => setAddAnchor(event.currentTarget)}
                aria-haspopup="menu"
                aria-expanded={Boolean(addAnchor)}
                sx={designShell.primaryButtonBase}
              >
                Add Reimbursement
              </Mui.Button>
            ) : null}
            {/* ⚙ -> Payroll Settings › Reimbursements */}
            <OpenSettingsButton group="pay-input" item="reimbursements" />
          </Mui.Stack>
        </Mui.Box>

        <Mui.Menu
          anchorEl={addAnchor}
          open={Boolean(addAnchor)}
          onClose={() => setAddAnchor(null)}
          elevation={0}
          PaperProps={{ sx: { ...designShell.actionMenuPaper, mt: 0.5 } }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
        >
          {addItems.map((item) => (
            <Mui.MenuItem key={item.label} sx={designShell.actionMenuItem} onClick={() => { setAddAnchor(null); item.onClick(); }}>
              <Mui.ListItemIcon><item.icon fontSize="small" sx={{ color: designColors.tealDark }} /></Mui.ListItemIcon>
              {item.label}
            </Mui.MenuItem>
          ))}
        </Mui.Menu>

        <Mui.Box sx={{ px: 2 }}>
          {list.error ? (
            <Mui.Alert severity="error" sx={{ mb: 1.5 }} action={<Mui.Button color="inherit" onClick={reload}>Retry</Mui.Button>}>
              Couldn't load the claims. {list.error}
            </Mui.Alert>
          ) : null}

          <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))", xl: "repeat(5, minmax(0, 1fr))" }, gap: 1.5, mb: 2 }}>
            {CARDS.map((card) => (
              <StatCard key={card.key} icon={card.icon} title={card.title} value={cardValue(summary?.[card.key])} accent={card.accent} tint={card.tint} />
            ))}
          </Mui.Box>

          <Mui.Box sx={{ position: "sticky", top: APP_BAR_OFFSET, zIndex: 4, backgroundColor: designColors.white }}>
            <CustomFilter
              title="Reimbursements"
              filterHeaders={claimHeaders}
              data={listRows}
              filteredData={visibleRows}
              onFilteredDataChange={onFilteredChange}
              screenAccess={true}
              hideCount={true}
              downloadFormats={["excel", "pdf", "csv"]}
              includeMetadata={true}
              metadata={{ "Company Name": topbarCompanyName || "N/A", "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss") }}
              labeledToolbar={true}
              searchPlaceholder="Search employee..."
            />
          </Mui.Box>

          <Mui.Box sx={{ ...designShell.sectionCard, mt: 1.5, borderRadius: 0 }}>
            <Mui.TableContainer sx={{ overflowX: "auto" }}>
              <Mui.Table size="small" sx={{ minWidth: 1100 }}>
                <Mui.TableHead>
                  <Mui.TableRow>
                    {claimHeaders.map((head) => (
                      <Mui.TableCell key={head.key} align={NUMERIC.has(head.key) ? "right" : "left"} sx={headSx}>{head.name}</Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {list.loading ? (
                    <Mui.TableRow><Mui.TableCell colSpan={claimHeaders.length} sx={{ borderBottom: "none" }}><Loader info="Loading..." minHeight="30vh" /></Mui.TableCell></Mui.TableRow>
                  ) : visibleRows.length === 0 ? (
                    <TableEmptyState dataLength={listRows} colSpan={claimHeaders.length} />
                  ) : pageRows.map((row) => (
                    <Mui.TableRow
                      key={row.id}
                      hover
                      onClick={() => setViewId(row.id)}
                      sx={{ cursor: "pointer", ...designShell.tableRowHover, ...(viewId === row.id ? designShell.neutralSelectedRow : {}) }}
                    >
                      {claimHeaders.map((head) => (
                        <Mui.TableCell key={head.key} align={NUMERIC.has(head.key) ? "right" : "left"} sx={cellSx}>
                          {renderCell(row, head.key)}
                        </Mui.TableCell>
                      ))}
                    </Mui.TableRow>
                  ))}
                </Mui.TableBody>
              </Mui.Table>
            </Mui.TableContainer>
            <Mui.Typography sx={{ ...designType.captionText, px: 2, py: 1.25 }}>
              Claims from My Workspace arrive here automatically. Ready claims are paid in their payroll cycle; * marks the cycle a Pending claim is expected in.
            </Mui.Typography>
            <Mui.Box sx={sharedStickyFooterBarSx}>
              {paginationFooter({
                page,
                rowsPerPage,
                total: visibleRows.length,
                itemLabel: "claims",
                onPageChange: setPage,
                onRowsPerPageChange: (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); },
                rowsPerPageOptions,
              })}
            </Mui.Box>
          </Mui.Box>
        </Mui.Box>
      </Mui.Box>

      {viewRow && !form ? (
        <ReimbursementViewPanel
          row={viewRow}
          domain={domain}
          companyId={topbarCompanyId}
          busy={busy}
          expanded={expanded}
          onToggleExpand={() => setExpanded((value) => !value)}
          onClose={() => setViewId(null)}
          onEdit={(claim) => { setFormErrors({}); setForm({ claim }); }}
          onCancel={(claim) => { setCancelRemarks(""); setCancelRow(claim); }}
        />
      ) : null}
      {form ? (
        <ReimbursementFormPanel
          key={form.claim?.id || "new"}
          claim={form.claim}
          domain={domain}
          companyId={topbarCompanyId}
          types={types}
          billUploadEnabled={settings?.billUploadEnabled !== false}
          approvalRequired={approvalRequired}
          saving={busy}
          serverErrors={formErrors}
          onClose={() => setForm(null)}
          onSave={saveClaim}
        />
      ) : null}
      {importOpen ? (
        <ReimbursementImport
          domain={domain}
          companyId={topbarCompanyId}
          types={types}
          onClose={() => setImportOpen(false)}
          // The wizard stays open on its result step (Done closes it).
          onImported={reload}
        />
      ) : null}

      <GlobalConfirmDialog
        open={Boolean(cancelRow)}
        title="Cancel this claim?"
        confirmLabel="Cancel claim"
        cancelLabel="Keep"
        loadingLabel="Cancelling..."
        danger
        loading={busy}
        onCancel={() => { if (!busy) setCancelRow(null); }}
        onConfirm={confirmCancel}
        reasonValue={cancelRemarks}
        onReasonChange={(value) => setCancelRemarks(String(value).slice(0, 255))}
        reasonPlaceholder="Remarks (optional), e.g. Entered twice"
      >
        {cancelRow ? `${cancelRow.requestNo} · ${cancelRow.employee.name} · ₹${money(shownAmount(cancelRow))} will be cancelled and removed from this list.` : null}
      </GlobalConfirmDialog>
    </ThemeProvider>
  );
};
